import _, { groupBy } from 'lodash';
import {
  getRowSpaceType,
  isStorageSupportSpace,
  uniqueRow,
  defaultRowValuesWithSeatCap,
} from 'spw-components/worksheet/utils';
import { camelize } from 'spw-shared/utils';

import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import {
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from '../projects/types';
import {
  IProjectSpaceType,
  ISpaceType,
  isProjectSpaceTypeSubcategory,
} from '../space_types/types';
import {
  IWorksheetRow,
  IChildWorksheetSpaceType,
  IWorksheetRowAPIObject,
} from './types';
import WorksheetRow from '../../../models/WorksheetRow';
import { PlainWorksheet } from '../../../models/Worksheet';

export const sortSpaceTypesAsWorksheetRows = (
  r1: WorksheetRow,
  r2: WorksheetRow,
  worksheet: PlainWorksheet
) => {
  const row1Index = worksheet.rows.findIndex(
    (row) => row.spaceTypeId === r1.spaceId
  );
  const row2Index = worksheet.rows.findIndex(
    (row) => row.spaceTypeId === r2.spaceId
  );

  return row1Index - row2Index;
};

export const sortRowsByPreviousId = (
  entry: IWorksheetRow[] | WorksheetRow[]
) => {
  if (!entry.length) return [];
  // break early if projectSpaceTypes haven't been populated
  if (!_.every(entry, (r: WorksheetRow | IWorksheetRow) => r.projectSpaceType))
    return entry;

  const storage = (entry as any[])
    .filter((r: WorksheetRow | IWorksheetRow) =>
      isStorageSupportSpace(r.spaceType)
    )
    .map((r) => ({ ...r, presorted: false }));
  const unsorted = (entry as any[]).filter(
    (r: WorksheetRow | IWorksheetRow) =>
      !isStorageSupportSpace(r.spaceType) && !r.child
  );
  let nullPreviousId = 0;
  let firstIndex;
  // initiate a map to determine previous id sorting
  const indexById: Record<number, number> = {};
  for (let i = 0; i < unsorted.length; i++) {
    // we check to see if there are more items than one with previous id === null, because if so it means the sorting is broken
    if (nullPreviousId > 1) {
      return entry;
    }
    if (!unsorted[i]?.projectSpaceType?.previousId) {
      if (!firstIndex) {
        firstIndex = i;
      }
      nullPreviousId += 1;
    }
    // push every item into the map where the previousId is the key, and the value is the current index
    indexById[unsorted[i]?.projectSpaceType?.previousId] = i;
  }

  const sorted = [];
  // start sorting by the project space type that has null for a previous id
  const first = unsorted[firstIndex];

  sorted.push({ ...first, presorted: true });
  let previous = first;
  for (let i = 1; i < unsorted.length; i++) {
    // get next by recalling the index from our indexById map
    const current = unsorted[indexById[previous?.projectSpaceType?.id]];
    // push it into the sorted array
    sorted.push({ ...current, presorted: true });
    // set the current element as the previous
    previous = current;
  }

  // last check to see if anything is screwed up, in which case we return the unsorted array
  if (
    _.some(sorted, (s) => {
      return !_(s).omit('presorted').keys().value().length;
    })
  ) {
    return entry;
  }
  return sorted.concat(storage);
};

export const reSortSpaceTypesAfterDelete = (
  oldSpaceTypes: IProjectSpaceType[],
  deletedSpaceType: IProjectSpaceType
) => {
  // get the space that comes after the current space and set the previousId of that space to the previousId of the deleted pst
  const nextSpaceType = _.find(
    oldSpaceTypes,
    ({ previousId }) => previousId === deletedSpaceType.id
  );
  // if there is no next space type, then the deleted space type was the last one in the list
  if (!nextSpaceType) {
    return { projectSpaceTypesToUpdate: [] };
  }
  // now we modify 'next space type' to have the previous id of the deleted space type
  nextSpaceType.previousId = deletedSpaceType.previousId;

  return { projectSpaceTypesToUpdate: [nextSpaceType] };
};

const hydrateRow = (
  row: IWorksheetRow,
  projectSpaceType: IProjectSpaceType,
  projectSpaceTypesCategories: IProjectSpaceTypeCategory[]
) => {
  if (projectSpaceType === undefined) return null;
  const spaceType = getRowSpaceType(row, projectSpaceType);

  let category;
  let subcategory: IProjectSpaceTypeSubcategory | null = null;

  if (isProjectSpaceTypeSubcategory(projectSpaceType.category)) {
    category = projectSpaceTypesCategories.find(
      (c) =>
        c.id ===
        (projectSpaceType.category as IProjectSpaceTypeSubcategory).parentId
    );

    subcategory = category?.subcategories?.find(
      (projectSpaceTypeSubcategory: IProjectSpaceTypeSubcategory) =>
        projectSpaceTypeSubcategory.id === projectSpaceType.category.id
    );
  } else {
    category = projectSpaceTypesCategories.find(
      (projectSpaceTypeCategory) =>
        projectSpaceTypeCategory.id === projectSpaceType.category.id
    );
  }

  return {
    ...row,
    projectSpaceType,
    category: category || projectSpaceType.category,
    subcategory,
    spaceType: spaceType as ISpaceType,
    spaceTypeId: spaceType.id,
  } as IWorksheetRow;
};

export const hydrateWorksheetRows = ({
  rows,
  projectSpaceTypes,
  childWorksheetSpaces,
  projectSpaceTypesCategories,
}: {
  rows: IWorksheetRowAPIObject[] | IWorksheetRow[];
  projectSpaceTypes: IProjectSpaceType[];
  childWorksheetSpaces: IChildWorksheetSpaceType[];
  projectSpaceTypesCategories: IProjectSpaceTypeCategory[];
}) => {
  const foundProjectSpaceTypesIds: number[] = [];
  const foundChildWorksheetSpaceTypesIds: number[] = [];

  if (!projectSpaceTypesCategories?.length || !projectSpaceTypes.length) {
    return [];
  }

  const hydrateChildRow = (
    row: IWorksheetRow,
    childWorksheetSpaceType: IChildWorksheetSpaceType
  ) => {
    const spaceType = getRowSpaceType(row, childWorksheetSpaceType);

    const parentProjectSpaceType = _.find(
      projectSpaceTypes,
      (s) => s?.spaceType?.id === childWorksheetSpaceType.spaceTypeId
    );

    if (
      !(spaceType as IChildWorksheetSpaceType)?.parent ||
      !parentProjectSpaceType
    ) {
      return null;
    }

    const newChildWorksheetSpaceType = {
      ...childWorksheetSpaceType,
    };
    delete newChildWorksheetSpaceType.parent.spaceTypeCategory;
    delete (spaceType as IChildWorksheetSpaceType).parent.spaceTypeCategory;

    let category;
    let subcategory: IProjectSpaceTypeSubcategory | null = null;

    if (isProjectSpaceTypeSubcategory(parentProjectSpaceType.category)) {
      category = projectSpaceTypesCategories?.find(
        (c) =>
          c.id ===
          (parentProjectSpaceType.category as IProjectSpaceTypeSubcategory)
            .parentId
      );

      subcategory = category?.subcategories?.find(
        (projectSpaceTypeSubcategory: IProjectSpaceTypeSubcategory) =>
          projectSpaceTypeSubcategory.id === parentProjectSpaceType.category.id
      );
    } else {
      category = projectSpaceTypesCategories.find(
        (projectSpaceTypeCategory) =>
          projectSpaceTypeCategory.id === parentProjectSpaceType.category.id
      );
    }

    return {
      ...row,
      spaceType: spaceType as IChildWorksheetSpaceType,
      projectSpaceType: {
        ...newChildWorksheetSpaceType,
        spaceType: spaceType as ISpaceType,
      },
      // TODO: Remove projectSpaceTypes from store when the user leaves the project details page
      category: category || parentProjectSpaceType.category,
      subcategory,
      spaceTypeId: spaceType.id,
    } as IWorksheetRow;
  };

  const childSpaceTypesGroupedByParent = groupBy(
    childWorksheetSpaces,
    (cws: IChildWorksheetSpaceType) => {
      return cws.parent.id;
    }
  );

  const childRowsGroupedBySpaceType = groupBy(
    rows
      .filter((row) => row.child && !row.removed)
      .map((row): {
        row: IWorksheetRow;
        childWorksheetSpaceType: IChildWorksheetSpaceType;
      } => {
        const childWorksheetSpaceType = camelize(childWorksheetSpaces).find(
          (cws: IChildWorksheetSpaceType) =>
            (row.spaceId || row.projectSpaceType?.id) === cws.id
        );

        return { row, childWorksheetSpaceType };
      }),
    ({ childWorksheetSpaceType }) => {
      return childWorksheetSpaceType.spaceTypeId;
    }
  );

  const hydratedRows = projectSpaceTypes.reduce(
    (worksheetRows, projectSpaceType: IProjectSpaceType) => {
      const rowsAssociatedWithSpaceType = rows.filter(
        (worksheetRow: IWorksheetRow) =>
          !worksheetRow.child &&
          (worksheetRow.projectSpaceType?.id ||
            (worksheetRow as IWorksheetRowAPIObject).projectSpaceTypeId) ===
            projectSpaceType.id
      );

      if (rowsAssociatedWithSpaceType.length) {
        foundProjectSpaceTypesIds.push(projectSpaceType.id);

        const projectSpaceTypeRows = rowsAssociatedWithSpaceType.map(
          (worksheetRow: IWorksheetRow) =>
            hydrateRow(
              worksheetRow,
              projectSpaceType,
              projectSpaceTypesCategories
            )
        );
        const childSpaceTypes =
          childSpaceTypesGroupedByParent[projectSpaceType.spaceType.id];

        if (childSpaceTypes) {
          const childRows =
            childRowsGroupedBySpaceType[projectSpaceType.spaceType.id];

          if (childRows) {
            childRows.forEach(
              ({ row: worksheetRow, childWorksheetSpaceType }) => {
                foundChildWorksheetSpaceTypesIds.push(
                  childWorksheetSpaceType.id
                );
                projectSpaceTypeRows.push(
                  hydrateChildRow(worksheetRow, childWorksheetSpaceType)
                );
              }
            );
          }
        }

        return worksheetRows.concat(projectSpaceTypeRows);
      }

      if (childRowsGroupedBySpaceType[projectSpaceType.spaceType.id]) {
        const childRows = childRowsGroupedBySpaceType[
          projectSpaceType.spaceType.id
        ].filter(({ row: worksheetRow }) => !worksheetRow.removed);

        worksheetRows = worksheetRows.concat(
          childRows.map(({ row: worksheetRow, childWorksheetSpaceType }) => {
            foundChildWorksheetSpaceTypesIds.push(childWorksheetSpaceType.id);
            return hydrateChildRow(worksheetRow, childWorksheetSpaceType);
          })
        );
      }

      return worksheetRows.concat([
        hydrateRow(
          {
            ...defaultRowValuesWithSeatCap(projectSpaceType.spaceType),
            child: false,
            removed: true,
          },
          projectSpaceType,
          projectSpaceTypesCategories
        ),
      ]);
    },
    []
  );

  // Groups the removed child rows by space type id to insert them in the correct order
  const removedChildRowsBySpaceTypeId = groupBy(
    camelize(childWorksheetSpaces)
      .filter(
        (cws: IChildWorksheetSpaceType) =>
          !foundChildWorksheetSpaceTypesIds.includes(cws.id)
      )
      .map((childWorksheetSpaceType: IChildWorksheetSpaceType) => {
        return hydrateChildRow(
          {
            ...defaultRowValuesWithSeatCap(childWorksheetSpaceType.parent),
            child: true,
            removed: true,
          },
          childWorksheetSpaceType
        );
      }),
    (row: IWorksheetRow) => row?.spaceType?.parent?.id
  );

  // TODO: Get list of unused space types from the backend
  Object.keys(removedChildRowsBySpaceTypeId).forEach((spaceTypeId) => {
    const id = parseInt(spaceTypeId, 10);
    // Find position of removed parent
    const parentRowIndex = hydratedRows.findLastIndex((r: IWorksheetRow) => {
      return r.spaceType.id === id && r.removed;
    });
    // Find the index of the parent row to insert the child rows below
    if (parentRowIndex > -1) {
      hydratedRows.splice(
        parentRowIndex + 1,
        0,
        ...removedChildRowsBySpaceTypeId[spaceTypeId]
      );
    }
  });

  const childRows = hydratedRows.filter((row) => row.child);

  const childRowsBySpaceType = groupBy(childRows, (row: IWorksheetRow) => {
    return row.projectSpaceType.parent.id;
  });

  return (
    _(hydratedRows)
      .compact()
      // group by category
      .groupBy((row: IWorksheetRow) => {
        if (row.subcategory) {
          return row.subcategory.id;
        }

        return row.category.id;
      })
      .values()
      // sort every group of rows by their project space type previous id
      .map((worksheetRows: IWorksheetRow[]) => {
        if (
          worksheetRows.length &&
          isIWCategoryOrSubcategory(worksheetRows[0].category)
        ) {
          const parentRows = sortRowsByPreviousId(worksheetRows);

          return parentRows.reduce(
            (rowsAndNestedRows, parentRow: IWorksheetRow) => {
              const nestedRows =
                childRowsBySpaceType[parentRow.projectSpaceType.spaceType.id];
              if (nestedRows) {
                return [...rowsAndNestedRows, parentRow, ...nestedRows];
              }
              return [...rowsAndNestedRows, parentRow];
            },
            []
          );
        }

        return sortRowsByPreviousId(worksheetRows);
      })
      // flatten back out
      .flatten()
      .uniqBy(uniqueRow)
      .value()
  );
};
